const finiteNumber = value => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const normalizeEpochMs = value => {
    const parsed = finiteNumber(value);
    if (parsed === null || parsed <= 0) return null;
    // Tolère un producteur qui enverrait des secondes Unix. Le producteur
    // Android actuel envoie System.currentTimeMillis().
    return parsed < 100_000_000_000 ? parsed * 1000 : parsed;
};

function nearestCue(cues, target, valueOf) {
    if (!Array.isArray(cues) || cues.length === 0 || !Number.isFinite(target)) return null;

    let low = 0;
    let high = cues.length;
    while (low < high) {
        const mid = (low + high) >>> 1;
        const value = valueOf(cues[mid]);
        if (!Number.isFinite(value) || value <= target) low = mid + 1;
        else high = mid;
    }

    const candidates = [cues[low - 1], cues[low]].filter(Boolean);
    let best = null;
    let bestDistance = Infinity;
    candidates.forEach(cue => {
        const value = valueOf(cue);
        const epochMs = normalizeEpochMs(cue?.ts);
        if (!Number.isFinite(value) || epochMs === null) return;
        const distance = Math.abs(value - target);
        if (distance < bestDistance) {
            best = { cue, epochMs };
            bestDistance = distance;
        }
    });
    return best;
}

/**
 * Convertit l'horloge native HLS en position sur l'horloge universelle de la
 * session. Les tags ID3 Android lient un PTS média à un timestamp Unix précis.
 */
export function mediaTimeToSessionElapsed({
    mediaTime,
    cues,
    sessionStartTs,
    fallbackOffset = 0,
}) {
    const time = finiteNumber(mediaTime);
    if (time === null) return null;

    const startTs = normalizeEpochMs(sessionStartTs);
    const match = nearestCue(cues, time, cue => finiteNumber(cue?.pts));
    if (startTs !== null && match) {
        const cuePts = finiteNumber(match.cue.pts);
        const mediaEpochMs = match.epochMs + (time - cuePts) * 1000;
        return Math.max(0, (mediaEpochMs - startTs) / 1000);
    }

    return Math.max(0, Number(fallbackOffset || 0) + time);
}

/** Convertit un instant de session en position seekable dans le média HLS. */
export function sessionElapsedToMediaTime({
    sessionElapsed,
    cues,
    sessionStartTs,
    fallbackOffset = 0,
}) {
    const elapsed = finiteNumber(sessionElapsed);
    if (elapsed === null) return null;

    const startTs = normalizeEpochMs(sessionStartTs);
    const targetEpochMs = startTs === null ? null : startTs + elapsed * 1000;
    const match = targetEpochMs === null
        ? null
        : nearestCue(cues, targetEpochMs, cue => normalizeEpochMs(cue?.ts));

    if (match) {
        const cuePts = finiteNumber(match.cue.pts);
        return cuePts + (targetEpochMs - match.epochMs) / 1000;
    }

    return elapsed - Number(fallbackOffset || 0);
}

/** Convertit des plages bufferisées HLS vers l'horloge universelle de session. */
export function mediaRangesToSessionElapsed({
    ranges,
    cues,
    sessionStartTs,
    fallbackOffset = 0,
}) {
    if (!Array.isArray(ranges)) return [];

    return ranges.map(range => {
        const start = mediaTimeToSessionElapsed({
            mediaTime: range?.start,
            cues,
            sessionStartTs,
            fallbackOffset,
        });
        const end = mediaTimeToSessionElapsed({
            mediaTime: range?.end,
            cues,
            sessionStartTs,
            fallbackOffset,
        });
        if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
        return {
            start: Math.max(0, Math.min(start, end)),
            end: Math.max(0, Math.max(start, end)),
        };
    }).filter(Boolean);
}

/** Quantité de média contiguë déjà disponible après la position demandée. */
export function bufferedAheadAt(ranges, mediaTime) {
    const time = finiteNumber(mediaTime);
    if (time === null || !Array.isArray(ranges) || ranges.length === 0) return 0;

    for (const range of ranges) {
        const start = finiteNumber(range?.start);
        const end = finiteNumber(range?.end);
        if (start === null || end === null) continue;
        if (time >= start - 0.25 && time <= end + 0.05) {
            return Math.max(0, end - Math.max(start, time));
        }
    }

    const firstRange = ranges[0];
    const firstStart = finiteNumber(firstRange?.start);
    const firstEnd = finiteNumber(firstRange?.end);
    if (firstStart !== null && firstEnd !== null && time <= firstStart && firstStart <= 0.5) {
        return Math.max(0, firstEnd - firstStart);
    }

    return 0;
}

/** Sélectionne l'unique position T consommée par la carte et la timeline. */
export function selectSharedClockPosition({
    mediaClockRunning,
    mediaPosition,
    isLive,
    wallClockPosition,
    eventsMax,
    timelinePosition,
}) {
    if (isLive && mediaClockRunning && Number.isFinite(mediaPosition)) {
        return Math.max(0, mediaPosition);
    }
    if (isLive) {
        return Math.max(0, Number(eventsMax) || 0, Number(wallClockPosition) || 0);
    }
    const replayPosition = Math.max(0, Number(timelinePosition) || 0);
    const replayMax = Number(eventsMax) || 0;
    return replayMax > 0 ? Math.min(replayPosition, replayMax) : replayPosition;
}
